import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { WorkspaceShell } from "./WorkspaceShell";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, DollarSign, TrendingUp, TrendingDown } from "lucide-react";

const CATEGORIES = [
  ["production", "Production"], ["labor", "Labor"], ["shipping", "Shipping"],
  ["installation", "Installation"], ["travel", "Travel"], ["misc", "Miscellaneous"],
];

const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

export default function FinanceWorkspace() {
  const [data, setData] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = () => api.get("/admin/finance").then(({ data }) => setData(data)).catch(() => toast.error("Could not load finance"));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    try {
      await api.delete(`/admin/expenses/${id}`);
      load();
    } catch { toast.error("Could not delete"); }
  };

  const tabs = [
    {
      value: "overview", label: "Overview",
      render: () => data ? (
        <div className="grid gap-8" data-testid="fin-overview">
          <div className="grid md:grid-cols-3 gap-px bg-[hsl(var(--line))]" data-testid="fin-kpis">
            <BigKpi label="Supplier Revenue" value={fmt(data.revenue.supplier_revenue)}
              sub={`${data.revenue.paid_prime_suppliers} paid supplier${data.revenue.paid_prime_suppliers === 1 ? "" : "s"} × ${fmt(data.revenue.supplier_package_price)}`}
              icon={TrendingUp} tone="text-emerald-700" />
            <BigKpi label="Total Expenses" value={fmt(data.expenses.total)}
              sub="Across all categories" icon={TrendingDown} tone="text-red-700" />
            <BigKpi label="Gross Profit"
              value={fmt(data.profit.gross_profit)}
              sub={data.profit.margin_pct != null ? `${data.profit.margin_pct}% margin` : "No revenue yet"}
              icon={DollarSign} tone={data.profit.gross_profit >= 0 ? "text-emerald-700" : "text-red-700"} />
          </div>

          <div>
            <div className="overline mb-4"><span className="accent-rule" />Expenses by Category</div>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-px bg-[hsl(var(--line))]">
              {CATEGORIES.map(([k, l]) => (
                <div key={k} className="bg-white p-4" data-testid={`fin-cat-${k}`}>
                  <div className="serif text-2xl tabular-nums">{fmt(data.expenses.by_category[k])}</div>
                  <div className="overline mt-2">{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : <div className="text-sm text-zinc-500">Loading…</div>,
    },
    {
      value: "expenses", label: "Expenses",
      render: () => data ? (
        <div className="grid gap-5" data-testid="fin-expenses">
          <div className="flex items-baseline justify-between">
            <div className="text-sm text-zinc-600 tabular-nums">
              {data.expenses.rows.length} expense{data.expenses.rows.length === 1 ? "" : "s"} · Total {fmt(data.expenses.total)}
            </div>
            <button onClick={() => setCreating(true)} className="btn-black h-9 px-4 text-xs" data-testid="fin-add-expense">
              <Plus size={12} /> Add Expense
            </button>
          </div>
          <div className="card-flat overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-left">
                <tr className="border-b border-zinc-200">
                  <Th>Date</Th><Th>Description</Th><Th>Vendor</Th><Th>Category</Th><Th className="text-right">Amount</Th><Th></Th>
                </tr>
              </thead>
              <tbody>
                {data.expenses.rows.map((e) => (
                  <tr key={e.id} className="border-b border-zinc-100 last:border-0" data-testid={`fin-row-${e.id}`}>
                    <Td className="text-xs tabular-nums">{e.date || "—"}</Td>
                    <Td>
                      <div className="font-medium">{e.description}</div>
                      {e.notes && <div className="text-xs text-zinc-500 mt-1">{e.notes}</div>}
                    </Td>
                    <Td>{e.vendor || <span className="text-zinc-400">—</span>}</Td>
                    <Td><span className="pill">{CATEGORIES.find(([k]) => k === e.category)?.[1] || e.category}</span></Td>
                    <Td className="text-right tabular-nums font-medium">{fmt(e.amount)}</Td>
                    <Td>
                      <button onClick={() => remove(e.id)} className="text-zinc-400 hover:text-red-600" data-testid={`fin-remove-${e.id}`} aria-label="Delete">
                        <Trash2 size={14} />
                      </button>
                    </Td>
                  </tr>
                ))}
                {data.expenses.rows.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-zinc-500">No expenses yet. Add one to start tracking.</td></tr>}
              </tbody>
            </table>
          </div>
          {creating && <CreateExpenseDialog onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />}
        </div>
      ) : <div className="text-sm text-zinc-500">Loading…</div>,
    },
  ];

  return (
    <WorkspaceShell
      title="Finance"
      subtitle="Supplier revenue, production costs, labor, shipping, travel — event profitability."
      tabs={tabs}
      dataTestId="ws-title-finance"
    />
  );
}

function BigKpi({ label, value, sub, icon: Icon, tone }) {
  return (
    <div className="bg-white p-6">
      <div className="flex items-center gap-2"><Icon size={16} className={tone} strokeWidth={1.4} /><span className="overline">{label}</span></div>
      <div className={`serif text-4xl mt-3 tabular-nums ${tone}`}>{value}</div>
      <div className="text-xs text-zinc-500 mt-2">{sub}</div>
    </div>
  );
}

function CreateExpenseDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({ description: "", category: "production", amount: "", vendor: "", date: "", notes: "", exhibitor_id: "" });
  const [busy, setBusy] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  useEffect(() => { api.get("/admin/supplier-projects").then(({ data }) => setSuppliers(data.projects || [])).catch(() => {}); }, []);
  const create = async () => {
    if (!form.description.trim()) return toast.error("Describe the expense.");
    if (!form.amount || Number(form.amount) <= 0) return toast.error("Enter a positive amount.");
    setBusy(true);
    try {
      const body = { ...form, amount: Number(form.amount) };
      if (!body.exhibitor_id) delete body.exhibitor_id;
      await api.post("/admin/expenses", body);
      toast.success("Expense added.");
      onCreated();
    } catch (e) {
      const raw = e?.response?.data?.detail;
      toast.error(typeof raw === "string" ? raw : "Could not create expense");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="fin-new-modal">
      <div className="bg-white max-w-md w-full p-6 md:p-8">
        <div className="overline mb-2"><span className="accent-rule" />New Expense</div>
        <div className="serif text-2xl mb-5">Add expense</div>
        <div className="grid gap-4">
          <F label="Description *"><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="fin-new-description" className="h-11 rounded-none" /></F>
          <div className="grid grid-cols-2 gap-4">
            <F label="Category">
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="h-11 rounded-none" data-testid="fin-new-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            <F label="Amount (USD) *"><Input type="number" step="0.01" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="fin-new-amount" className="h-11 rounded-none" /></F>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <F label="Vendor"><Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} data-testid="fin-new-vendor" className="h-11 rounded-none" /></F>
            <F label="Date"><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="fin-new-date" className="h-11 rounded-none" /></F>
          </div>
          <F label="Notes"><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-none" data-testid="fin-new-notes" /></F>
          <F label="Supplier (optional)">
            <Select value={form.exhibitor_id || "none"} onValueChange={(v) => setForm({ ...form, exhibitor_id: v === "none" ? "" : v })}>
              <SelectTrigger className="h-11 rounded-none" data-testid="fin-new-supplier"><SelectValue placeholder="No supplier" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No supplier</SelectItem>
                {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.company_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </F>
        </div>
        <div className="mt-6 flex gap-3 justify-end">
          <button onClick={onClose} className="h-10 px-5 border border-zinc-300 text-xs uppercase tracking-[0.18em]" data-testid="fin-new-cancel">Cancel</button>
          <button onClick={create} disabled={busy} className="btn-black h-10 px-5" data-testid="fin-new-save">
            {busy ? "…" : "Save Expense"}
          </button>
        </div>
      </div>
    </div>
  );
}

const Th = ({ children, className = "" }) => <th className={`text-xs uppercase tracking-[0.18em] text-zinc-500 px-4 py-3 ${className}`}>{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-4 align-top ${className}`}>{children}</td>;
function F({ label, children }) { return (<div><Label className="overline">{label}</Label><div className="mt-2">{children}</div></div>); }
